/* --------------------------------------------------------------------------
  Copyright (c) 2016, VoiceVibes, Inc

  VoiceVibes, Inc - PROPRIETARY/CONFIDENTIAL
  All contents of this file are proprietary and confidential. No part of
  any of the contents of this file may be disclosed in any manner to
  a third party without the prior written consent of VoiceVibes, Inc.
  Unauthorized copying of this file, via any medium is strictly prohibited.
----------------------------------------------------------------------------- */

import _ from 'lodash';
import service from '../../services/groups';

const GROUPS_ALL = 'GROUPS_ALL';
const GROUPS_ONE = 'GROUPS_ONE';
const GROUP_MEMBERS = 'GROUP_MEMBERS';
const GROUP_RECORDINGS = 'GROUP_RECORDINGS';

const state = {
  list: null,
  members: {},
  recordings: {},
};

const mutations = {
  [GROUPS_ALL] (state, groups) {
    state.list = groups;
  },
  [GROUPS_ONE] (state, groupData) {
    const group = _.find(state.list, g => g.groupId === groupData.groupId);
    if (group) {
      _.assign(group, groupData);
    } else {
      state.list.push(groupData);
    }
  },
  [GROUP_MEMBERS] (state, [groupId, members]) {
    // updates list without losing in mem details
    state.members[groupId] = _.keyBy(members, m => m.userId);
  },
  [GROUP_RECORDINGS] (state, [groupId, recordings]) {
    state.recordings[groupId] = [];
    recordings.forEach((r) => {
      if (!state.recordings[groupId][r.userId]) {
        state.recordings[groupId][r.userId] = [];
      }
      state.recordings[groupId][r.userId].push(r);
    });
  }
};

let listGroupMembersCache = 0;

const actions = {
  listGroupMembers({ commit, getters }, adminUsers=false) {
    const groups = getters.groups;
    const groupMembers = getters.groupMembers;
    let groupIds = [];
    let lookups = [];

    if (adminUsers) {
      // Always reset cache for the admin
      listGroupMembersCache = 0;
    }

    // Return a fulfilled promise if the data is already loaded and less than 5 minutes old
    if (!_.isEmpty(groupMembers) && (Math.ceil(Date.now() / 1000) - listGroupMembersCache < 300)) {
      return window.Promise.resolve(true);
    }

    listGroupMembersCache = Math.ceil(Date.now() / 1000);

    if (groups && groups.length) {
      groups.forEach((group) => {
        groupIds.push(group.groupId);
        lookups.push(service.listMembers(group.groupId));
      });
    }
    if (adminUsers) {
      groupIds.push('00000000-0000-4000-0000-000000000000');
      lookups.push(service.listMembers('00000000-0000-4000-0000-000000000000'));
    }
    return window.Promise.all(lookups).then((members) => {
      if (members && members.length) {
        members.forEach((memberList, i) => {
          commit(GROUP_MEMBERS, [groupIds[i], memberList]);
        });
      }
    });
  },
  resetGroupMembersCache: () => {
    listGroupMembersCache = 0;
  },
  updateGroup: ({ commit }, group) => {
    return service.update(group.groupId, group)
      .then(() => {
        commit(GROUPS_ONE, group);
      }).catch(() => {}); // this will show an alert automatically
  },
  addGroup: ({ commit }, { name, orgName }) => {
    const inviteCode = (orgName + ' ' + name).replace(/[^a-z0-9]+/gi, '-')
      .replace(/(^-+|-+$)/g, '').substr(0,50);
    const newGroup = {
      name,
      inviteCode,
    };
    return service.add(newGroup)
      .then((_group) => {
        commit(GROUPS_ONE, _group);
      });
  },
  joinGroup: ({ }, {inviteCode}) => {
    return service.joinGroup({inviteCode});
  },
  listGroups: ({ commit }) => {
    return service.list()
      .then(groups => {
        commit(GROUPS_ALL, groups);
        return groups;
      })
      .catch(() => {}); // this will show an alert automatically
  },
  listGroupRecordings: ({ commit }, {groupId}) => {
    return service.listRecordings(groupId)
      .then(recordings => {
        commit(GROUP_RECORDINGS, [groupId, recordings]);
        return recordings;
      })
      .catch(() => {}); // this will show an alert automatically
  },
  resetGroups: ({ commit }) => {
    commit(GROUPS_ALL, []);
  }
};

const getters = {
  groups: state => state.list,
  groupMembers: state => state.members,
  groupRecordings: state => state.recordings,
  activeGroups: state => _.sortBy(
    _.filter(state.list, group => group.membershipStatus === 'active'), g => -new Date(g.createdDate).getTime()
  ),
  activeGroupsById: (state) => {
    let groups = {};
    _.map(
      _.filter(state.list, group => group.membershipStatus === 'active'),
      (g) => groups[g.groupId] = g
    );
    return groups;
  },
};

export default {
  state,
  mutations,
  actions,
  getters
};
